import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { issuePlayerCommand } from '@/api/commands';
import { adminWs, type CommandStatusEvent } from '@/api/ws-admin';
import type { PlayerCommandIssue } from '@displaycms/contracts';
import { toast } from '@/lib/toast';

/**
 * P5 W1 §2.2 — Operator command controls for `/admin/players/:id`.
 *
 * Renders four affordances on the detail page:
 *   - pause / resume       (`player.pause_toggle`)
 *   - clear cache          (`player.clear_cache`, preserving pairing)
 *   - capture screenshot   (`player.capture_screenshot`)
 *   - volume slider        (`player.set_volume`, commits on
 *                           mouseup/touchend so dragging doesn't
 *                           spam the wire)
 *
 * Live ACK feedback comes from `adminWs.onCommandStatus`. When a
 * `capture_screenshot` completion arrives with a screenshot_id, the
 * parent is notified via `onScreenshotReady(commandId)` so it can
 * open the viewer modal.
 */

export interface PlayerCommandControlsProps {
  playerId: string;
  onScreenshotReady: (commandId: string) => void;
}

type BusyKey = 'pause' | 'clear' | 'shot' | 'vol' | null;

export function PlayerCommandControls({
  playerId,
  onScreenshotReady,
}: PlayerCommandControlsProps): JSX.Element {
  const { t } = useTranslation(['players']);
  const [busy, setBusy] = useState<BusyKey>(null);
  const [volume, setVolume] = useState<number>(1);

  useEffect(() => {
    // Ensure the admin-namespace socket is connected. The singleton is
    // idempotent — repeat calls are a no-op when already connected — but
    // this component is mounted on `/players/:id` directly (deep link or
    // refresh), bypassing the list/pair routes that previously owned the
    // `adminWs.connect()` call. Without this line the listener registers
    // against a never-opened socket, no `command.status` events arrive,
    // and the screenshot viewer modal never fires.
    adminWs.connect();
    const off = adminWs.onCommandStatus((evt: CommandStatusEvent) => {
      if (evt.player_id !== playerId) return;
      if (evt.status === 'completed' || evt.status === 'ok') {
        setBusy(null);
        // Use a stable toast id so Sonner updates (replaces) the existing
        // success toast rather than stacking a new one. Without this, rapid
        // sequential commands accumulate multiple matching toasts, which
        // breaks Playwright's strict-mode locator assertion (>1 element).
        toast.success(t('players:cmd.toast_completed'), { id: 'cmd-ack' });
        const detail = evt.detail;
        if (
          detail &&
          typeof detail === 'object' &&
          'screenshot_id' in detail &&
          typeof (detail as { screenshot_id?: unknown }).screenshot_id ===
            'string'
        ) {
          onScreenshotReady(evt.command_id);
        }
      } else if (
        evt.status === 'failed' ||
        evt.status === 'rejected' ||
        evt.status === 'unsupported' ||
        evt.status === 'stale'
      ) {
        setBusy(null);
        toast.error(t('players:cmd.toast_failed', { status: evt.status }));
      }
    });
    return off;
  }, [playerId, t, onScreenshotReady]);

  const issue = async (cmd: PlayerCommandIssue, key: Exclude<BusyKey, null>): Promise<void> => {
    setBusy(key);
    try {
      await issuePlayerCommand(playerId, cmd);
      // Optimistic UI: leave `busy` set until the WS ACK clears it.
    } catch (err) {
      setBusy(null);
      toast.error(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <section
      aria-labelledby="cmd-heading"
      className="rounded-md border border-border bg-card p-4"
      data-testid="player-command-controls"
    >
      <h2
        id="cmd-heading"
        className="mb-3 text-sm font-medium text-muted-foreground"
      >
        {t('players:cmd.title')}
      </h2>
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          data-testid="cmd-pause-toggle"
          disabled={busy === 'pause'}
          onClick={() =>
            void issue(
              { command_type: 'player.pause_toggle', args: {} },
              'pause',
            )
          }
          className="rounded-md border border-input bg-background px-3 py-2 text-sm disabled:opacity-50"
        >
          {busy === 'pause'
            ? t('players:cmd.sending')
            : t('players:cmd.pause_toggle')}
        </button>

        <button
          type="button"
          data-testid="cmd-clear-cache"
          disabled={busy === 'clear'}
          onClick={() =>
            void issue(
              {
                command_type: 'player.clear_cache',
                args: { preserve_pairing: true },
              },
              'clear',
            )
          }
          className="rounded-md border border-input bg-background px-3 py-2 text-sm disabled:opacity-50"
        >
          {busy === 'clear'
            ? t('players:cmd.sending')
            : t('players:cmd.clear_cache')}
        </button>

        <button
          type="button"
          data-testid="cmd-screenshot"
          disabled={busy === 'shot'}
          onClick={() =>
            void issue(
              {
                command_type: 'player.capture_screenshot',
                args: {
                  max_dimension: 1920,
                  format: 'jpeg',
                  max_bytes: 2_097_152,
                },
              },
              'shot',
            )
          }
          className="rounded-md border border-input bg-background px-3 py-2 text-sm disabled:opacity-50"
        >
          {busy === 'shot'
            ? t('players:cmd.sending')
            : t('players:cmd.capture_screenshot')}
        </button>

        <label className="flex items-center gap-2 text-sm" htmlFor="cmd-volume-input">
          <span>{t('players:cmd.volume')}</span>
          <input
            id="cmd-volume-input"
            data-testid="cmd-volume-input"
            type="range"
            min={0}
            max={1}
            step={0.05}
            value={volume}
            onChange={(e) => setVolume(parseFloat(e.target.value))}
            onMouseUp={() =>
              void issue(
                { command_type: 'player.set_volume', args: { level: volume } },
                'vol',
              )
            }
            onTouchEnd={() =>
              void issue(
                { command_type: 'player.set_volume', args: { level: volume } },
                'vol',
              )
            }
          />
          <span data-testid="cmd-volume-value">{Math.round(volume * 100)}%</span>
        </label>
      </div>

    </section>
  );
}

export default PlayerCommandControls;
